import { EntryCard } from '@/components/shop/EntryCard'
import { ShopOptionList } from '@/components/shop/ShopOptionList'
import { shopKey } from '@/components/shop/shopOptions'
import { authApi } from '@/lib/api'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import toast from 'react-hot-toast'

interface ShopInfo {
  shop_no: number
  shop_name: string
  primary_domain?: string
  default?: string
}

export const Route = createFileRoute('/shop-select')({
  component: ShopSelectPage,
  validateSearch: (search) => ({
    mall_id: search.mall_id as string,
    shops: search.shops as string,
    // OAuth 콜백이 서명한 단명 티켓 — 등록 요청의 유일한 증명이다.
    // 없거나 만료되면 백엔드가 403 shop_select_ticket_invalid 로 거절한다.
    ticket: (search.ticket as string | undefined) ?? '',
  }),
})

function ShopSelectPage() {
  const { mall_id, shops, ticket } = Route.useSearch()
  const navigate = useNavigate()
  const shopList = decodeShops(shops)
  // 카페24 '기본' 샵을 미리 골라 둔다 — 대부분 그 샵을 고르므로 버튼 한 번이면 끝난다.
  // 등록은 확인 버튼을 거쳐야 일어나니 미리 선택해도 실수로 등록되지는 않는다.
  const [selectedShopNo, setSelectedShopNo] = useState<number | null>(
    () => shopList.find((s) => s.default === 'T')?.shop_no ?? null,
  )
  const [loading, setLoading] = useState(false)
  // 티켓 만료·위조(403) — 같은 화면에서 다시 눌러도 계속 실패하므로 안내 화면으로 바꾼다
  const [ticketExpired, setTicketExpired] = useState(false)

  const handleRegister = async () => {
    if (selectedShopNo === null) {
      toast.error('샵을 선택해주세요')
      return
    }

    setLoading(true)
    try {
      const result = await authApi.registerShop(
        mall_id,
        selectedShopNo,
        ticket,
      )

      if (result.success) {
        // 토큰 저장
        authApi.setToken(result.token)
        toast.success(
          `${result.shop_name || `샵 #${result.shop_no}`} 등록되었어요.`,
        )

        // 대시보드로 이동
        navigate({ to: '/' })
      }
    } catch (error: any) {
      // 백엔드 한도 거절(403 shop_limit_exceeded) / 확인 실패(503
      // shop_limit_check_unavailable)는 OAuth 콜백 경로와 동일한 안내 페이지로
      // 보내 UX를 일치시킨다 (영어 에러 코드 토스트 방지).
      const detail = error?.response?.data?.detail
      if (detail === 'shop_limit_exceeded') {
        navigate({
          to: '/install-not-eligible',
          search: { mall_id, reason: 'shop_limit_exceeded' },
        })
        return
      }
      if (detail === 'shop_limit_check_unavailable') {
        navigate({
          to: '/install-not-eligible',
          search: { mall_id, reason: 'temporarily_unavailable' },
        })
        return
      }
      // 티켓 만료(30분)·위조. 원인은 구분하지 않고 재실행만 안내한다.
      if (detail === 'shop_select_ticket_invalid') {
        setTicketExpired(true)
        return
      }
      toast.error(error.message || '샵 등록에 실패했어요.')
    } finally {
      setLoading(false)
    }
  }

  if (ticketExpired) {
    return (
      <EntryCard
        title="샵 선택 시간이 지났어요"
        description="보안을 위해 샵 선택은 30분 동안만 할 수 있어요. 카페24 관리자에서 앱을 다시 실행해 주세요."
        footer={`쇼핑몰 ID · ${mall_id}`}
      />
    )
  }

  // 샵 목록이나 티켓이 없는 경우 — 티켓 없이 고르게 두면 등록이 반드시 거절된다
  if (shopList.length === 0 || !ticket) {
    return (
      <EntryCard
        title="샵 정보를 불러올 수 없어요"
        description="카페24 관리자에서 앱을 다시 실행해 주세요."
      />
    )
  }

  return (
    <EntryCard
      title="사용할 샵을 골라 주세요"
      description={
        <>
          이 쇼핑몰은 처음 연결해요. 다른 샵은 이용 가능한 샵 수 안에서,
          나중에 그 샵의 카페24 관리자에서 앱을 실행하면 추가돼요.
        </>
      }
      footer={`쇼핑몰 ID · ${mall_id}`}
    >
      {/* 샵 목록 — 행 모양은 캠퍼스 진입·헤더 전환기와 공유 (REQ-54).
          등록은 샵 한도를 쓰는 되돌릴 수 없는 일이라 고른 뒤 버튼을 한 번 더 누른다. */}
      <div className="mb-6">
        <ShopOptionList
          items={shopList.map((shop) => ({
            mall_id,
            shop_no: shop.shop_no,
            shop_name: shop.shop_name,
            primary_domain: shop.primary_domain,
            is_default: shop.default === 'T',
          }))}
          mode="radio"
          ariaLabel="등록할 샵"
          selectedKey={
            selectedShopNo === null
              ? null
              : shopKey({ mall_id, shop_no: selectedShopNo })
          }
          onSelect={(item) => setSelectedShopNo(item.shop_no)}
          disabled={loading}
          trailing={(_, selected) => <RadioMark selected={selected} />}
        />
      </div>

      {/* 등록 버튼 */}
      <button
        type="button"
        onClick={handleRegister}
        disabled={selectedShopNo === null || loading}
        className={`w-full min-h-[44px] py-3 px-4 rounded-xl font-medium transition-colors ${
          selectedShopNo === null || loading
            ? 'bg-surface-low text-on-surface-variant cursor-not-allowed'
            : 'bg-primary text-white hover:bg-primary-dim'
        }`}
      >
        {loading ? '연결 중...' : '이 샵으로 시작하기'}
      </button>
    </EntryCard>
  )
}

/** shops 파라미터 디코딩 (URL-safe base64 → standard base64 → JSON). 실패하면 빈 목록. */
function decodeShops(shops: string | undefined): ShopInfo[] {
  try {
    const standardBase64 = (shops ?? '').replace(/-/g, '+').replace(/_/g, '/')
    const parsed = JSON.parse(atob(standardBase64))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** 라디오 선택 표시 (체크 원) */
function RadioMark({ selected }: { selected: boolean }) {
  return (
    <span
      className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
        selected ? 'bg-primary text-white' : 'border border-outline-variant'
      }`}
      aria-hidden
    >
      {selected && (
        <svg
          className="w-3 h-3"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="2.5 6.5 5 9 9.5 3.5" />
        </svg>
      )}
    </span>
  )
}
